import { Badge, cn } from "@gotts.ai/ui";
import { AddressDisplay } from "@gotts.ai/ui";
import { ChevronDown, Search, Star } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { TokenIcon } from "../../components/TokenIcon";

import { GROUP_COLORS, GROUP_ORDER } from "./types";

import type { ContractGroup, ContractOption } from "./types";

interface ContractSelectorProps {
  options: ContractOption[];
  selected: string;
  onSelect: (value: string) => void;
  favorites: Set<string>;
  onToggleFavorite: (value: string) => void;
  onCustom: () => void;
}

export function ContractSelector({
  options,
  selected,
  onSelect,
  favorites,
  onToggleFavorite,
  onCustom,
}: ContractSelectorProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  // Click outside to close
  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => {
      document.removeEventListener("mousedown", handleClick);
    };
  }, [open]);

  // Focus search when opening
  useEffect(() => {
    if (open) searchRef.current?.focus();
  }, [open]);

  const selectedOpt = options.find((o) => o.value === selected);
  const filtered = search
    ? options.filter(
        (o) =>
          o.label.toLowerCase().includes(search.toLowerCase()) ||
          o.address.toLowerCase().includes(search.toLowerCase()),
      )
    : options;

  // Group options
  const pinnedOptions = filtered.filter((o) => favorites.has(o.value));
  const grouped = new Map<ContractGroup, ContractOption[]>();
  for (const group of GROUP_ORDER) {
    if (group === "Pinned") continue;
    const items = filtered.filter((o) => o.group === group);
    if (items.length > 0) grouped.set(group, items);
  }

  return (
    <div ref={containerRef} className="relative">
      {/* Trigger */}
      <button
        type="button"
        onClick={() => {
          setOpen(!open);
        }}
        className={cn(
          "flex w-full items-center gap-2.5 px-4 py-3 text-left transition-all duration-300",
          "bg-[rgba(20,20,18,0.65)]",
          "border border-[rgba(201,168,76,0.12)]",
          "shadow-[4px_4px_0_rgba(0,0,0,0.30),inset_0_1px_0_rgba(201,168,76,0.06),inset_0_-1px_0_rgba(0,0,0,0.05)]",
          open
            ? "border-[rgba(201,168,76,0.40)] shadow-[5px_5px_0_rgba(0,0,0,0.40),inset_0_1px_0_rgba(201,168,76,0.12),inset_0_-1px_0_rgba(0,0,0,0.05)]"
            : "hover:border-[rgba(201,168,76,0.25)] hover:shadow-[5px_5px_0_rgba(0,0,0,0.35),inset_0_1px_0_rgba(201,168,76,0.10),inset_0_-1px_0_rgba(0,0,0,0.05)]",
        )}
      >
        {selectedOpt ? (
          <>
            {selectedOpt.tokenSymbol ? (
              <TokenIcon symbol={selectedOpt.tokenSymbol} size={20} />
            ) : (
              <span className="flex h-5 w-5 items-center justify-center bg-[rgba(255,255,255,0.06)] text-[10px] font-bold text-[var(--color-text-muted)]">
                {selectedOpt.label[0]}
              </span>
            )}
            <span className="font-sans text-sm font-medium text-[var(--color-text)]">
              {selectedOpt.label}
            </span>
            <Badge
              className={cn(
                "ml-1 text-[9px]",
                GROUP_COLORS[selectedOpt.group] ??
                  "bg-gray-500/20 text-gray-400",
              )}
            >
              {selectedOpt.group}
            </Badge>
            <span className="ml-auto">
              <AddressDisplay
                address={selectedOpt.address}
                leadChars={6}
                tailChars={4}
                fontSize={10}
              />
            </span>
          </>
        ) : (
          <>
            <Search size={14} className="text-[var(--color-text-muted)]" />
            <span className="text-sm text-[var(--color-text-muted)]">
              Search contracts...
            </span>
          </>
        )}
        <ChevronDown
          size={14}
          className={cn(
            "shrink-0 text-[var(--color-text-muted)] transition-transform duration-200",
            open && "rotate-180",
          )}
        />
      </button>

      {/* Dropdown */}
      {open && (
        <div className="absolute left-0 right-0 top-full z-50 mt-1 max-h-[400px] overflow-auto border border-[rgba(201,168,76,0.18)] bg-[rgba(16,16,14,0.95)] shadow-[5px_5px_0_rgba(0,0,0,0.45)]">
          {/* Search */}
          <div className="sticky top-0 z-10 border-b border-[rgba(201,168,76,0.10)] bg-[rgba(16,16,14,0.95)] p-2">
            <div className="flex items-center gap-2 bg-[rgba(0,0,0,0.3)] px-2 py-1.5">
              <Search
                size={13}
                className="shrink-0 text-[var(--color-text-muted)]"
              />
              <input
                ref={searchRef}
                type="text"
                placeholder="Search contracts..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Escape") {
                    setOpen(false);
                    setSearch("");
                  }
                }}
                className="w-full bg-transparent text-xs text-[var(--color-text)] outline-none placeholder:text-[var(--color-text-muted)]"
              />
            </div>
          </div>

          {/* Pinned section */}
          {pinnedOptions.length > 0 && (
            <div>
              <div className="section-label px-3 pb-1 pt-2">Pinned</div>
              {pinnedOptions.map((opt) => (
                <OptionRow
                  key={opt.value}
                  opt={opt}
                  isSelected={opt.value === selected}
                  isFavorite={true}
                  onSelect={() => {
                    onSelect(opt.value);
                    setOpen(false);
                    setSearch("");
                  }}
                  onToggleFavorite={() => {
                    onToggleFavorite(opt.value);
                  }}
                />
              ))}
              <div className="mx-3 border-b border-[rgba(201,168,76,0.10)]" />
            </div>
          )}

          {/* Grouped sections */}
          {[...grouped.entries()].map(([group, items]) => (
            <div key={group}>
              <div className="section-label px-3 pb-1 pt-2">{group}</div>
              {items.map((opt) => (
                <OptionRow
                  key={opt.value}
                  opt={opt}
                  isSelected={opt.value === selected}
                  isFavorite={favorites.has(opt.value)}
                  onSelect={() => {
                    onSelect(opt.value);
                    setOpen(false);
                    setSearch("");
                  }}
                  onToggleFavorite={() => {
                    onToggleFavorite(opt.value);
                  }}
                />
              ))}
            </div>
          ))}

          {/* Custom option */}
          <div className="border-t border-[rgba(201,168,76,0.10)]">
            <button
              type="button"
              onClick={() => {
                onCustom();
                setOpen(false);
                setSearch("");
              }}
              className="flex w-full items-center gap-2 px-3 py-2.5 text-left text-xs text-[var(--color-text-muted)] transition-colors hover:bg-[rgba(201,168,76,0.03)] hover:text-[var(--color-gold)]"
            >
              <span className="border-[var(--color-text-muted)]/30 flex h-5 w-5 items-center justify-center border border-dashed text-[10px]">
                +
              </span>
              Custom Address...
            </button>
          </div>

          {filtered.length === 0 && (
            <div className="px-3 py-4 text-center text-xs text-[var(--color-text-muted)]">
              No contracts match "{search}"
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Option row
// ---------------------------------------------------------------------------

function OptionRow({
  opt,
  isSelected,
  isFavorite,
  onSelect,
  onToggleFavorite,
}: {
  opt: ContractOption;
  isSelected: boolean;
  isFavorite: boolean;
  onSelect: () => void;
  onToggleFavorite: () => void;
}) {
  return (
    <div
      className={cn(
        "flex items-center gap-2 px-3 py-1.5 transition-colors hover:bg-[rgba(201,168,76,0.03)]",
        isSelected && "bg-[rgba(201,168,76,0.05)]",
      )}
    >
      <button
        type="button"
        onClick={onSelect}
        className="flex min-w-0 flex-1 items-center gap-2"
      >
        {opt.tokenSymbol ? (
          <TokenIcon symbol={opt.tokenSymbol} size={20} />
        ) : (
          <span className="flex h-5 w-5 shrink-0 items-center justify-center bg-[rgba(255,255,255,0.06)] text-[9px] font-bold text-[var(--color-text-muted)]">
            {opt.label[0]}
          </span>
        )}
        <span className="truncate text-xs font-medium text-[var(--color-text)]">
          {opt.label}
        </span>
        <span className="font-mono text-[10px] text-[var(--color-text-muted)]">
          <AddressDisplay
            address={opt.address}
            leadChars={6}
            tailChars={4}
            fontSize={10}
          />
        </span>
        <Badge className="ml-auto shrink-0 text-[8px]">
          {opt.contractType}
        </Badge>
      </button>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onToggleFavorite();
        }}
        className="shrink-0 p-0.5"
        title={isFavorite ? "Unpin" : "Pin"}
      >
        <Star
          size={12}
          className={cn(
            "transition-colors",
            isFavorite
              ? "fill-yellow-400 text-yellow-400"
              : "text-[var(--color-text-muted)]/30 hover:text-yellow-400/50",
          )}
        />
      </button>
    </div>
  );
}
